import { ConfigurationError } from '@cian/peperrors/shared';
import * as React from 'react';

import { wrapDisplayName } from './displayName';

interface IMakeContextModuleOptions<T> {
  contextName: string;
  defaultValue?: T;
}

type TMapContextToProps<CP, T> = (context: T) => CP;
type TMergeProps<P, CP> = (ownProps: Omit<P, keyof CP>, contextProps: CP) => P;

export function makeContextModule<T>(options: IMakeContextModuleOptions<T>) {
  const { defaultValue = null, contextName } = options;

  const Context = React.createContext<T>(defaultValue as T);
  Context.displayName = contextName;

  const useContext = () => {
    const context = React.useContext(Context);

    if (!context) {
      throw new ConfigurationError({
        message: `use${contextName} used outside of it's context`,
        domain: contextName,
      });
    }

    return context;
  };

  const withContext = function <CP, P extends CP>(
    mapContextToProps: TMapContextToProps<CP, T>,
    mergeProps?: TMergeProps<P, CP>,
  ) {
    return (Component: React.ComponentType<P>) => {
      const displayName = wrapDisplayName(Component, `with${contextName}`);

      class WithContext extends React.Component<P & { context: T }> {
        public static displayName = displayName;
        public static contextType = Context;

        public render() {
          const { context } = this;

          if (!context) {
            throw new ConfigurationError({
              message: `${displayName} used outside of it's context`,
              domain: contextName,
            });
          }

          const contextProps = mapContextToProps(context);
          const resultProps = (mergeProps && mergeProps(this.props, contextProps)) || {
            ...contextProps,
            ...this.props,
          };

          return <Component {...resultProps} />;
        }
      }

      return WithContext;
    };
  };

  return {
    Context,
    useContext,
    withContext,
  };
}
